import { Injectable } from '@angular/core';

/**
 * The table-settings surface a {@link TrTableComponent} exposes to the gear
 * button. Declared here (rather than importing the component) so the registry
 * stays free of a circular import with the table it tracks.
 */
export interface TrTableSettingsHost {
  /** Drop this device's column layout and fall back to the location default. */
  resetToLocationDefault: () => void;
  /** Publish this device's column layout as the location's default. */
  saveAsLocationDefault: () => void;
  /** Open the show/hide columns modal. */
  openColumnsModal: () => void;
}

/**
 * Lookup from `stateId` (`'waitlist'`, `'history'`, …) to the live table.
 *
 * The table settings button sits in the page toolbar, above and outside the
 * table — often outside the `*ngIf` that renders it — so it can't reach the
 * table with a template reference. Instead each table registers itself under
 * its `stateId` and the button resolves it on click, which also means a page
 * whose list is currently empty (no table in the DOM) simply resolves nothing
 * instead of erroring.
 */
@Injectable({ providedIn: 'root' })
export class TrTableRegistryService {
  private readonly hosts = new Map<string, TrTableSettingsHost>();

  register(stateId: string | null | undefined, host: TrTableSettingsHost): void {
    if (stateId) {
      this.hosts.set(stateId, host);
    }
  }

  /** Only clears the entry when `host` is still the registered table. */
  unregister(stateId: string | null | undefined, host: TrTableSettingsHost): void {
    if (stateId && this.hosts.get(stateId) === host) {
      this.hosts.delete(stateId);
    }
  }

  get(stateId: string | null | undefined): TrTableSettingsHost | undefined {
    return stateId ? this.hosts.get(stateId) : undefined;
  }
}
